import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { useDispatch } from '../../../Flux/index.ts'
import { orderWizardActions as wizard } from '../../../Flux/Slice/OrderWizard/OrderWizardSlice.ts'
import type { DraftState } from '../../../Model/OrderWizard/OrderWizardDraft.ts'
import type { Board } from '../../../Services/OrderWizard/poolBoard.ts'
import { appliedAnswer } from '../../../Services/OrderWizard/poolBoard.ts'

/** Where a leave held for unapplied pool changes was asked from: the prompt shows there. */
export type LeaveOrigin = 'stepper' | 'footer'

export type PendingPoolEdit = {
  /** The board's moves not applied yet; `undefined` while the pools are as the draft has them. */
  edit: Board | undefined
  setEdit: (edit: Board | undefined) => void
  /** Whether there are changes to apply that leaving must ask about. */
  pending: boolean
  /** "Use these pools": the edit becomes the draft's pools answer. */
  apply: () => void
  discard: () => void
  /** Leaves at once, or, with changes pending, holds the leave until the lab applies, discards or stays. */
  guard: (leave: () => void, origin: LeaveOrigin) => void
  leaving: LeaveOrigin | undefined
  /** Applies or discards the changes, then leaves as held. */
  resolveLeave: (apply: boolean) => void
  stay: () => void
}

const PendingPoolEditContext = createContext<PendingPoolEdit | undefined>(undefined)

/**
 * The pool edit's state for one draft. `guarding` is whether the edit is shown, so leaving may lose it;
 * a change of `resetKey` (the draft, the step) drops the edit and any held leave.
 */
export const usePendingPoolEditState = (draft: DraftState, guarding: boolean, resetKey: string): PendingPoolEdit => {
  const dispatch = useDispatch()
  const [edit, setEdit] = useState<Board | undefined>()
  const [held, setHeld] = useState<{ leave: () => void; origin: LeaveOrigin } | undefined>()

  useEffect(() => {
    setEdit(undefined)
    setHeld(undefined)
  }, [resetKey])

  return useMemo(() => {
    const pending = guarding && !!edit
    const apply = () => {
      if (edit) {
        dispatch(wizard.questionAnswered({ draftId: draft.id, answer: appliedAnswer(draft, edit) }))
      }
      setEdit(undefined)
    }
    const discard = () => setEdit(undefined)
    return {
      edit,
      setEdit,
      pending,
      apply,
      discard,
      guard: (leave, origin) => (pending ? setHeld({ leave, origin }) : leave()),
      leaving: pending ? held?.origin : undefined,
      resolveLeave: (applied) => {
        if (!held) {
          return
        }
        if (applied) {
          apply()
        } else {
          discard()
        }
        setHeld(undefined)
        held.leave()
      },
      stay: () => setHeld(undefined)
    }
  }, [draft, guarding, edit, held, dispatch])
}

export const PendingPoolEditContextProvider = PendingPoolEditContext.Provider

/** Holds a draft's pool edit for what it wraps: the steps, or a test's pools step. */
export const PendingPoolEditProvider = ({ draft, children }: { draft: DraftState; children: ReactNode }) => (
  <PendingPoolEditContext.Provider value={usePendingPoolEditState(draft, true, draft.id)}>{children}</PendingPoolEditContext.Provider>
)

export const usePendingPoolEdit = (): PendingPoolEdit => {
  const value = useContext(PendingPoolEditContext)
  if (!value) {
    throw new Error('usePendingPoolEdit needs a PendingPoolEditProvider')
  }
  return value
}
